import React, { FC, ReactNode, useEffect, useMemo, useRef, useState, isValidElement } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SvgIconTypeMap } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';

import { buttonVariants, buttonWidths } from '@clatter/ui';
import { isDarkColor } from '../../../helpers';
import { CircularLoader } from '../../../shared';

const StyledButton = styled.button`
  border-radius: ${({ theme }) => theme.radius.large};
  padding: 10px 40px;
  height: 40px;
  border: 1px solid;

  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  transition: background-color 0.3s ease, filter 0.3s ease;

  &.${buttonWidths.full} {
    width: 100%;
    span {
      margin: 0 auto;
    }
  }

  &:hover:not(:disabled) {
    background-color: #48c5eb;
  }
  &:disabled {
    background-color: #b3eafc;
    cursor: not-allowed;
  }

  &.button-primary {
    background-color: ${({ theme }) => theme?.palette?.primaryColor};
    color: ${({ theme }) => (isDarkColor(theme?.palette?.primaryColor) ? theme?.palette?.white : theme?.palette?.text)};
    border-color: ${({ theme }) => theme?.palette?.primaryColor};
    &:hover:not(:disabled) {
      filter: brightness(0.95);
    }
  }
  &.button-secondary {
    background-color: ${({ theme }) => theme?.palette?.secondaryColor};
    border-color: ${({ theme }) => theme?.palette?.secondaryColor};
    color: #fff;
  }

  &.${buttonVariants.clear} {
    color: #262626;
    background-color: #fff;
    border-color: ${({ theme }) =>
      isDarkColor(theme?.palette?.darkGrey) ? theme?.palette?.white : theme?.palette?.darkGrey};
    &:hover:not(:disabled) {
      background-color: #fff;
      border-color: ${({ theme }) => theme?.palette?.accent};
      transition: all 0.3s ease-in-out;
    }
  }

  &.${buttonVariants.danger} {
    background-color: ${({ theme }) => theme?.palette?.danger};
    border-color: ${({ theme }) => theme?.palette?.danger};
    &:hover:not(:disabled) {
      background-color: #fff;
      color: ${({ theme }) => theme?.palette?.danger};
    }
  }

  &.${buttonVariants.dark} {
    background-color: ${({ theme }) => theme?.palette?.darkGrey};
    border-color: ${({ theme }) => theme?.palette?.darkGrey};
    &:hover:not(:disabled) {
      background-color: #fff;
      color: ${({ theme }) => theme?.palette?.darkGrey};
    }
  }

  &.${buttonVariants.success} {
    background-color: ${({ theme }) => theme?.palette?.success};
    border-color: ${({ theme }) => theme?.palette?.success};
    &:hover:not(:disabled) {
      background-color: #fff;
      color: ${({ theme }) => theme?.palette?.success};
    }
  }

  &.${buttonVariants.warning} {
    background-color: ${({ theme }) => theme?.palette?.warning};
    border-color: ${({ theme }) => theme?.palette?.warning};
    &:hover:not(:disabled) {
      background-color: #fff;
      color: ${({ theme }) => theme?.palette?.warning};
    }
  }

  &:disabled {
    border-color: ${({ theme }) => theme?.palette?.border};
    color: ${({ theme }) => theme?.palette?.border};
    background-color: #fff;
  }

  &.${buttonWidths.sm} {
    padding: 0 ${({ theme }) => theme?.spacing?.medium};
  }
  &.${buttonWidths.md} {
    min-width: 10rem;
    padding: 0 ${({ theme }) => theme?.spacing?.large};
  }
  &.${buttonWidths.lg} {
    padding: 0 66px;
  }

  .button-icon {
    font-size: 20px;
    + span {
      margin-left: ${({ theme }) => theme?.spacing?.small};
    }
  }

  &.has-menu-items {
    padding-left: 20px;
    padding-right: 0;
  }

  .loader-container {
    svg {
      color: ${({ theme }) => theme?.palette?.white};
    }
  }

  &.is-loading {
    pointer-events: none;
    padding: 0;
  }
`;

const StyledMenuContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme?.spacing?.small || '8px'};
  padding: 0 16px;
`;

const StyledButtonIcon = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-left: 1px solid ${({ theme }) => theme?.palette?.darkGrey};
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
`;

const StyledMenu = styled.div`
  position: absolute;
  display: none;
  background-color: #fff;
  border-radius: 8px;
  z-index: 2;

  width: max-content;
  white-space: nowrap;

  top: 46px;
  right: 0;
  border: 1px solid ${({ theme }) => theme?.palette?.grey};
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);

  &.opened {
    display: block;
  }
`;

const StyledMenuItem = styled.div`
  cursor: pointer;
  padding: ${({ theme }) => `${theme?.spacing?.medium} ${theme?.spacing?.medium}`};
  font-size: 14px;

  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme?.spacing?.medium || '10px'};

  &:hover {
    background-color: ${({ theme }) => theme?.palette?.hoverBackgroundColor};
  }

  &.disabled {
    color: ${({ theme }) => theme?.palette?.border};
    cursor: not-allowed;
  }

  .menu-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    font-size: 18px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const StyledMenuDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme?.palette?.grey};
  margin: 4px 0;
`;

type MenuAction = {
  type: string; // e.g., "link" | "upload" | "delete" | ...
  payload?: Record<string, any>;
};

export type NgButtonMenuItem = {
  label?: string;
  action?: MenuAction; // always { type, payload }
  testId?: string;
  divider?: boolean; // render a non-interactive separator when true
  icon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> | React.ComponentType<any> | ReactNode;
};

interface NgButtonProps {
  children?: ReactNode;
  disabled?: boolean;
  variant?: string;
  width?: string;
  testId?: string;
  isLoading?: boolean;
  icon?: any;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (action?: MenuAction) => void; // returns the action object
  menuItems?: NgButtonMenuItem[];
}

export const NgButton: FC<NgButtonProps> = ({
  children,
  onClick,
  type = 'button',
  isLoading,
  width = buttonWidths.md,
  variant = buttonVariants.primary,
  disabled = false,
  testId,
  icon: Icon = null,
  menuItems,
}) => {
  const [opened, setOpened] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const hasMenu = useMemo(() => Array.isArray(menuItems) && menuItems.length > 0, [menuItems]);
  const actionableItems = useMemo(() => (menuItems || []).filter((i) => !i.divider), [menuItems]);
  const hasMultipleMenuItems = hasMenu && actionableItems.length > 1;

  const handleRootClick = () => {
    if (disabled) return;
    if (!hasMenu) {
      onClick?.();
      return;
    }
    if (hasMultipleMenuItems) {
      setOpened((prev) => !prev);
      return;
    }
    // Single actionable item: return its action
    const only = actionableItems[0];
    onClick?.(only?.action);
  };

  const handleMenuItemClick = (action?: MenuAction) => {
    if (disabled) return;
    onClick?.(action);
    setOpened(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (opened && ref.current && !ref.current.contains(e.target as Node)) setOpened(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [opened]);

  return (
    <StyledMenuContainer ref={ref}>
      <StyledButton
        className={classNames(variant, width, { 'is-loading': isLoading, 'has-menu-items': hasMultipleMenuItems })}
        disabled={disabled}
        type={type}
        onClick={handleRootClick}
        data-test-id={testId}
        aria-haspopup={hasMultipleMenuItems ? 'menu' : undefined}
        aria-expanded={hasMultipleMenuItems ? opened : undefined}
      >
        {isLoading ? (
          <span className="loader-container">
            <CircularLoader size={20} />
          </span>
        ) : (
          <>
            {!!Icon && <Icon className="button-icon" />}

            <StyledButtonContent>{!!children && <span>{children}</span>}</StyledButtonContent>

            {hasMultipleMenuItems && (
              <StyledButtonIcon className={variant} aria-hidden>
                {opened ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </StyledButtonIcon>
            )}
          </>
        )}
      </StyledButton>

      {hasMultipleMenuItems && (
        <StyledMenu className={classNames({ opened })} role="menu">
          {menuItems!.map(({ label, action, divider, testId, icon: ItemIcon }, index) => {
            if (divider) {
              return <StyledMenuDivider key={`divider-${index}`} role="separator" aria-orientation="horizontal" />;
            }

            let iconNode: ReactNode = null;
            if (ItemIcon) {
              if (isValidElement(ItemIcon)) {
                iconNode = <span className="menu-icon">{ItemIcon}</span>;
              } else if (typeof ItemIcon === 'function' || typeof ItemIcon === 'object') {
                const AsComponent: any = ItemIcon as any;
                iconNode = <AsComponent className="menu-icon" />;
              }
            }

            return (
              <StyledMenuItem
                key={`item-${index}`}
                className={classNames({ disabled })}
                onClick={() => !disabled && handleMenuItemClick(action)}
                data-test-id={testId}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMenuItemClick(action);
                  }
                }}
              >
                {iconNode}
                {label}
              </StyledMenuItem>
            );
          })}
        </StyledMenu>
      )}
    </StyledMenuContainer>
  );
};
